import type { SupabaseClient } from "@supabase/supabase-js";

import {
  removeOrphanProductFeedbackScreenshot,
  uploadProductFeedbackScreenshot,
} from "./product-feedback-attachment";

/** v1.1 keeps the v1 RPC live for old bundles and uses this additive screenshot-aware contract. */
export const SUBMIT_PRODUCT_FEEDBACK_RPC = "submit_product_feedback_v11";

export const PRODUCT_FEEDBACK_TYPES = ["problem", "idea"] as const;
export type ProductFeedbackType = (typeof PRODUCT_FEEDBACK_TYPES)[number];

export const PRODUCT_FEEDBACK_PROBLEM_KINDS = [
  "broken",
  "unexpected",
  "slow",
  "confusing",
] as const;
export type ProductFeedbackProblemKind = (typeof PRODUCT_FEEDBACK_PROBLEM_KINDS)[number];

export const PRODUCT_FEEDBACK_TITLE_MAX = 120;
export const PRODUCT_FEEDBACK_MESSAGE_MAX = 3_000;

/**
 * Reviewable diagnostic categories. Values are deliberately plain strings: no arbitrary nested
 * object can smuggle raw events, tokens, coordinates, or other unreviewed context into the RPC.
 */
export interface ProductFeedbackSystemInfo {
  readonly app_build?: string;
  readonly browser_device?: string;
  readonly page?: string;
  readonly account_role?: string;
  readonly session?: string;
  readonly error_codes?: readonly string[];
}

export type ProductFeedbackSystemInfoKey = keyof ProductFeedbackSystemInfo;

export interface ProductFeedbackDraft {
  readonly type: ProductFeedbackType;
  readonly problemKinds: readonly ProductFeedbackProblemKind[];
  readonly title: string;
  readonly message: string;
  readonly systemInfo: ProductFeedbackSystemInfo;
  readonly allowContact: boolean;
}

export type ProductFeedbackValidation =
  | {
      readonly ok: true;
      readonly title: string | null;
      readonly message: string | null;
      readonly problemKinds: readonly ProductFeedbackProblemKind[];
    }
  | { readonly ok: false; readonly reason: string };

export function validateProductFeedbackDraft(
  draft: ProductFeedbackDraft,
  options: { readonly hasScreenshot?: boolean } = {},
): ProductFeedbackValidation {
  const title = draft.title.trim();
  const message = draft.message.trim();
  const problemKinds =
    draft.type === "problem" ? [...new Set(draft.problemKinds)].sort() : [];

  if (title.length > PRODUCT_FEEDBACK_TITLE_MAX) {
    return {
      ok: false,
      reason: `Keep the idea name under ${PRODUCT_FEEDBACK_TITLE_MAX} characters.`,
    };
  }
  if (message.length > PRODUCT_FEEDBACK_MESSAGE_MAX) {
    return {
      ok: false,
      reason: `Keep the note under ${PRODUCT_FEEDBACK_MESSAGE_MAX.toLocaleString()} characters.`,
    };
  }
  if (
    title.length === 0 &&
    message.length === 0 &&
    problemKinds.length === 0 &&
    Object.keys(draft.systemInfo).length === 0 &&
    options.hasScreenshot !== true
  ) {
    return {
      ok: false,
      reason: "Add a short note, screenshot, or include at least one system detail.",
    };
  }

  return {
    ok: true,
    title: draft.type === "idea" && title.length > 0 ? title : null,
    message: message.length > 0 ? message : null,
    problemKinds,
  };
}

export interface ProductFeedbackEnvironment {
  readonly appVersion: string;
  readonly userAgent: string;
  readonly viewport: string;
  readonly path: string;
  readonly online: boolean;
  readonly errorCodes: readonly string[];
}

export function collectProductFeedbackSystemInfo({
  environment,
  accountName,
  roleLabel,
  pageLabel,
}: {
  environment: ProductFeedbackEnvironment;
  accountName: string;
  roleLabel: string;
  pageLabel: string;
}): Required<ProductFeedbackSystemInfo> {
  return {
    app_build: `Console · ${environment.appVersion}`,
    browser_device: `${environment.userAgent} · ${environment.viewport}`,
    page: `${pageLabel} · ${environment.path}`,
    account_role: `${accountName} · ${roleLabel}`,
    session: environment.online ? "Online · standard features" : "Offline · standard features",
    error_codes: environment.errorCodes.slice(-3),
  };
}

export const SAFE_PRODUCT_FEEDBACK_FAILURE =
  "We couldn't send this right now. Please try again.";

export const KNOWN_PRODUCT_FEEDBACK_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to send feedback.",
  "Account id is required to send feedback.",
  "Active account membership is required to send feedback.",
  "Feedback type must be problem or idea.",
  "Problem kind is not recognized.",
  "Idea name is too long.",
  "Feedback note is too long.",
  "System information is not valid.",
  "Add a short note or include at least one system detail.",
  "Add a short note, screenshot, or include at least one system detail.",
  "Screenshot information is not valid.",
  "Please wait a moment before sending another report.",
]);

export type ProductFeedbackWriteOutcome =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: string };

function safeFailureReason(error: unknown): string {
  const message =
    error !== null &&
    typeof error === "object" &&
    typeof (error as { message?: unknown }).message === "string"
      ? (error as { message: string }).message
      : "";
  return KNOWN_PRODUCT_FEEDBACK_FAILURES.has(message)
    ? message
    : SAFE_PRODUCT_FEEDBACK_FAILURE;
}

export async function submitProductFeedback(
  client: SupabaseClient,
  accountId: string,
  draft: ProductFeedbackDraft,
  screenshot: File | null = null,
): Promise<ProductFeedbackWriteOutcome> {
  const validation = validateProductFeedbackDraft(draft, {
    hasScreenshot: screenshot !== null,
  });
  if (!validation.ok) return validation;

  const screenshotResult =
    screenshot === null
      ? null
      : await uploadProductFeedbackScreenshot(client, screenshot);
  if (screenshotResult !== null && !screenshotResult.ok) return screenshotResult;
  const uploadedScreenshot = screenshotResult?.upload ?? null;

  try {
    const { error } = await client.rpc(SUBMIT_PRODUCT_FEEDBACK_RPC, {
      p_account_id: accountId,
      p_feedback_type: draft.type,
      p_problem_kinds: validation.problemKinds,
      p_title: validation.title,
      p_message: validation.message,
      p_system_info: draft.systemInfo,
      p_allow_contact: draft.allowContact,
      p_screenshot_path: uploadedScreenshot?.path ?? null,
      p_screenshot_mime_type: uploadedScreenshot?.mimeType ?? null,
      p_screenshot_size_bytes: uploadedScreenshot?.sizeBytes ?? null,
    });
    if (error === null) return { ok: true };
    if (uploadedScreenshot !== null) {
      await removeOrphanProductFeedbackScreenshot(client, uploadedScreenshot.path);
    }
    return { ok: false, reason: safeFailureReason(error) };
  } catch {
    if (uploadedScreenshot !== null) {
      await removeOrphanProductFeedbackScreenshot(client, uploadedScreenshot.path);
    }
    return { ok: false, reason: SAFE_PRODUCT_FEEDBACK_FAILURE };
  }
}
